import type { CSSProperties, ReactNode } from 'react';
import { IconArrowRight } from '@tabler/icons-react';

/**
 * Shared pieces of the Buat Aju choice cards (Kepabeanan + Manifest): a tinted
 * icon tile and a tinted "Mulai" button whose arrow loops while the card is
 * hovered. Both expect the card to carry Tailwind's `group` class.
 */

/** 14% / 22% tints of a 6-digit hex colour, as a hex alpha suffix. */
export const tint = (hex: string) => `${hex}24`;
export const tintStrong = (hex: string) => `${hex}38`;

/**
 * Direction colours: Inward/Import blue, Outward/Export orange, FTZ teal.
 * Dark values are softened (clear blue, ochre, sage) so they don't glow on the
 * navy cards; they match the manifest dashboard's inward/outward tones.
 */
export const CHOICE_COLORS = {
  inward: { light: '#0369A1', dark: '#62A6EE' },
  outward: { light: '#C2410C', dark: '#DDA15E' },
  ftz: { light: '#0F766E', dark: '#7DB39F' },
} as const;

export const choiceColor = (key: keyof typeof CHOICE_COLORS, isDark: boolean) =>
  isDark ? CHOICE_COLORS[key].dark : CHOICE_COLORS[key].light;

interface ChoiceTileProps {
  color: string;
  /** Tile edge in px; the icon inside is sized by the caller. */
  size: number;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function ChoiceTile({ color, size, children, className = '', style }: ChoiceTileProps) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size / 4),
        backgroundColor: tint(color),
        color,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function ChoiceCta({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[color:var(--cta-bg)] px-3.5 py-2 text-sm font-semibold transition-colors duration-150 group-hover:bg-[color:var(--cta-bg-hover)]"
      style={{ '--cta-bg': tint(color), '--cta-bg-hover': tintStrong(color), color } as CSSProperties}
    >
      {children}
      {/* Fixed, clipped box: the arrow leaves right and re-enters left without resizing the button. */}
      <span className="inline-flex h-4 w-4 overflow-hidden" aria-hidden="true">
        <IconArrowRight size={16} stroke={2} className="group-hover:animate-arrow-loop motion-reduce:animate-none" />
      </span>
    </span>
  );
}
